import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { useTaxonomy } from "@/context/TaxonomyContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, MapPin, Briefcase, Bookmark, IndianRupee, Clock, Building2 } from "lucide-react";

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tax = useTaxonomy();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [kw, setKw] = useState(params.get("keyword") || "");
  const [loc, setLoc] = useState(params.get("location") || "");
  const [dept, setDept] = useState("");
  const [product, setProduct] = useState("");
  const [empType, setEmpType] = useState("");

  const fetchJobs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const q = { page: pg, page_size: 10 };
      if (kw) q.keyword = kw;
      if (loc) q.location = loc;
      if (dept) q.department = dept;
      if (product) q.product = product;
      if (empType) q.employment_type = empType;
      const res = await api.get("/jobs", { params: q });
      setJobs(res.data.jobs);
      setTotal(res.data.total);
      setPage(pg);
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  }, [kw, loc, dept, product, empType]);

  useEffect(() => { fetchJobs(1); }, [dept, product, empType]);
  useEffect(() => { fetchJobs(1); /* eslint-disable-next-line */ }, []);

  const applySearch = () => {
    const p = new URLSearchParams();
    if (kw) p.set("keyword", kw);
    if (loc) p.set("location", loc);
    setParams(p);
    fetchJobs(1);
  };

  const toggleSave = async (e, id) => {
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    try {
      const r = await api.post(`/jobs/${id}/save`);
      setJobs((js) => js.map((j) => j.job_id === id ? { ...j, saved: r.data.saved } : j));
      toast.success(r.data.saved ? "Job saved" : "Removed from saved");
    } catch { toast.error("Please sign in"); }
  };

  return (
    <PublicLayout>
      <div className="bg-slate-900 hero-grid">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <h1 className="font-display text-3xl font-bold text-white mb-5">Cable & Conductor Industry Jobs</h1>
          <div className="bg-white rounded-xl p-2 flex flex-col sm:flex-row gap-2 shadow-xl">
            <div className="flex items-center gap-2 flex-1 px-3">
              <Search className="w-5 h-5 text-slate-400" />
              <input data-testid="jobs-search-keyword" value={kw} onChange={(e) => setKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} placeholder="Title, skill, product" className="w-full py-2.5 outline-none text-sm" />
            </div>
            <div className="flex items-center gap-2 sm:w-52 px-3 sm:border-l border-slate-200">
              <MapPin className="w-5 h-5 text-slate-400" />
              <input data-testid="jobs-search-location" value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} placeholder="Location" className="w-full py-2.5 outline-none text-sm" />
            </div>
            <button data-testid="jobs-search-btn" onClick={applySearch} className="copper-gradient text-white font-semibold px-6 py-2.5 rounded-lg">Search</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Department</label>
            <select data-testid="filter-department" value={dept} onChange={(e) => setDept(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white">
              <option value="">All</option>
              {(tax.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Product</label>
            <select data-testid="filter-product" value={product} onChange={(e) => setProduct(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white">
              <option value="">All</option>
              {(tax.products || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Employment Type</label>
            <select data-testid="filter-emptype" value={empType} onChange={(e) => setEmpType(e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 bg-white">
              <option value="">All</option>
              {(tax.employment_types || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <button onClick={() => { setDept(""); setProduct(""); setEmpType(""); }} className="text-sm text-[#D96B27] font-medium">Clear filters</button>
        </aside>

        {/* Results */}
        <div>
          <p className="text-sm text-slate-500 mb-4" data-testid="jobs-count">{total} jobs found</p>
          {loading ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white rounded-xl border border-slate-200 animate-pulse" />)}</div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
              <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No jobs match your search.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => (
                <div key={j.job_id} data-testid={`job-card-${j.job_id}`} onClick={() => navigate(`/jobs/${j.job_id}`)} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-[#D96B27] hover:shadow-md transition-all cursor-pointer">
                  <div className="flex justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-display font-semibold text-lg text-slate-900 hover:text-[#D96B27]">{j.title}</h3>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1"><Building2 className="w-4 h-4" />{j.company_name}</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-slate-400" />{j.location}</span>
                        <span className="flex items-center gap-1"><Briefcase className="w-4 h-4 text-slate-400" />{j.min_experience}-{j.max_experience} yrs</span>
                        {j.salary_min && <span className="flex items-center gap-1"><IndianRupee className="w-4 h-4 text-slate-400" />{j.salary_min}-{j.salary_max} LPA</span>}
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-slate-400" />{j.employment_type}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(j.products || []).slice(0, 4).map((p) => <span key={p} className="text-xs bg-[#FFF3EC] text-[#C05615] px-2 py-0.5 rounded">{p}</span>)}
                        {(j.skills || []).slice(0, 3).map((p) => <span key={p} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{p}</span>)}
                      </div>
                    </div>
                    <button data-testid={`job-save-${j.job_id}`} onClick={(e) => toggleSave(e, j.job_id)} className="shrink-0 h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:border-[#D96B27]">
                      <Bookmark className={`w-4 h-4 ${j.saved ? "fill-[#D96B27] text-[#D96B27]" : "text-slate-400"}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {total > 10 && (
            <div className="flex justify-center gap-2 mt-6">
              <button disabled={page === 1} onClick={() => fetchJobs(page - 1)} className="px-4 py-2 text-sm border rounded-md disabled:opacity-40 bg-white">Prev</button>
              <span className="px-4 py-2 text-sm">Page {page} of {Math.ceil(total / 10)}</span>
              <button disabled={page >= Math.ceil(total / 10)} onClick={() => fetchJobs(page + 1)} className="px-4 py-2 text-sm border rounded-md disabled:opacity-40 bg-white">Next</button>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
